import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import TryOurTools from '../components/TryOurTools';

const getVideoId = (input: string): string | null => {
  try {
    const url = new URL(input);
    if (url.hostname.endsWith('youtube.com')) {
      return url.searchParams.get('v');
    }
    if (url.hostname === 'youtu.be') {
      return url.pathname.slice(1);
    }
  } catch {
    if (/^[a-zA-Z0-9_-]{11}$/.test(input)) return input;
  }
  return null;
};

const EmbedCodeGenerator: React.FC = () => {
  const [input, setInput] = useState('');
  const [autoplay, setAutoplay] = useState(false);
  const [start, setStart] = useState('');
  const [loop, setLoop] = useState(false);
  const [controls, setControls] = useState(true);
  const [embedCode, setEmbedCode] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setEmbedCode('');
    const id = getVideoId(input.trim());
    if (!id) {
      setError('Please enter a valid YouTube URL or video ID.');
      return;
    }
    let params = [];
    if (autoplay) params.push('autoplay=1');
    if (start && /^\d+$/.test(start)) params.push(`start=${start}`);
    if (!controls) params.push('controls=0');
    if (loop) params.push('loop=1', `playlist=${id}`);
    const paramString = params.length ? `?${params.join('&')}` : '';
    const code = `<iframe width="560" height="315" src="https://www.youtube.com/embed/${id}${paramString}" title="YouTube video player" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe>`;
    setEmbedCode(code);
  };

  return (
    <>
      <Helmet>
        <title>YouTube Embed Code Generator - Free Tool</title>
        <meta name="description" content="Generate responsive YouTube embed code with custom options. No login or API required." />
        <meta name="keywords" content="youtube embed code, youtube embed generator, responsive youtube embed, youtube iframe code" />
        <link rel="canonical" href="/embed-code-generator" />
      </Helmet>
      <div className="max-w-xl mx-auto py-10">
        <h1 className="text-3xl font-bold mb-2 text-light-text dark:text-dark-text text-center">YouTube Embed Code Generator</h1>
        <p className="text-center text-light-subtle dark:text-dark-subtle mb-8">Generate responsive YouTube embed code with custom options. 100% free, no login needed.</p>
        <form onSubmit={handleSubmit} className="flex flex-col gap-3 mb-6">
          <input
            type="text"
            value={input}
            onChange={e => setInput(e.target.value)}
            placeholder="Paste YouTube URL or video ID"
            className="px-4 py-3 rounded-lg border border-light-border dark:border-dark-border bg-light-card dark:bg-dark-card text-light-text dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-light-accent dark:focus:ring-dark-accent placeholder-light-subtle dark:placeholder-dark-subtle"
            autoFocus
          />
          <div className="flex flex-wrap gap-4">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={autoplay} onChange={e => setAutoplay(e.target.checked)} /> Autoplay
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={loop} onChange={e => setLoop(e.target.checked)} /> Loop
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={controls} onChange={e => setControls(e.target.checked)} /> Show Controls
            </label>
            <input
              type="number"
              value={start}
              onChange={e => setStart(e.target.value)}
              placeholder="Start (s)"
              className="w-28 px-2 py-2 rounded-lg border border-light-border dark:border-dark-border bg-light-card dark:bg-dark-card text-light-text dark:text-dark-text focus:outline-none focus:ring-1 focus:ring-light-accent dark:focus:ring-dark-accent placeholder-light-subtle dark:placeholder-dark-subtle"
              min={0}
            />
          </div>
          <button
            type="submit"
            className="px-6 py-3 rounded-lg bg-light-accent text-white dark:bg-dark-accent dark:text-dark-background font-semibold text-base hover:bg-light-accent_hover dark:hover:bg-dark-accent_hover transition-colors"
          >
            Generate Embed Code
          </button>
        </form>
        {error && <div className="text-red-600 dark:text-red-400 mb-4 text-center">{error}</div>}
        {embedCode && (
          <div className="bg-black/5 dark:bg-white/5 rounded-lg p-6 mt-6 flex flex-col items-center">
            <div className="mb-2 w-full">
              <textarea
                className="w-full p-2 rounded border border-light-border dark:border-dark-border bg-light-background dark:bg-dark-background text-light-text dark:text-dark-text text-xs font-mono resize-none"
                rows={4}
                value={embedCode}
                readOnly
                onFocus={e => e.currentTarget.select()}
              />
            </div>
            <div className="text-xs text-light-subtle dark:text-dark-subtle">Copy and paste this code into your website.</div>
          </div>
        )}
        <div className="mt-8 mb-2 p-4 rounded-lg bg-gradient-to-r from-red-100 to-purple-100 dark:from-red-900/30 dark:to-purple-900/30 border border-light-border dark:border-dark-border text-light-text dark:text-dark-text shadow">
          <strong>YouTube Embed Code Generator:</strong> Instantly create responsive <b>YouTube embed code</b> for any video. Customize options like autoplay, start time, loop, and controls. No login or API required. Perfect for bloggers, webmasters, and anyone who wants to <b>embed YouTube videos</b> on their website quickly and easily. 100% free and SEO-friendly.
        </div>
        <TryOurTools />
      </div>
    </>
  );
};

export default EmbedCodeGenerator; 